import { useEffect, useMemo, useRef, useState} from 'react';
import L from 'leaflet';
import {SimpleMapScreenshoter} from 'leaflet-simple-map-screenshoter'
import * as turf from '@turf/turf';

import { MapContainer, TileLayer, Polyline, CircleMarker, useMap, Polygon, SVGOverlay } from 'react-leaflet';
import { getSeismicEventColor, surveyToCoordinates } from './RTFOMapWithWellbore';
import { useSelector } from 'react-redux';
import { selectRTFO } from '../../reducers/rtfoSlice';
import RTFOMapLegend from './RTFOMapLegend';

const RTFOSeismicReportMap = (({ seismicReportData, setSeismicReportMapVisible, setSeismicReportMap }) => {
  
	const mapRef = useRef(null);
	const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

	const wellCoordinates = useMemo(() => {
    try {
      const coords = {};
      const wells = currentRTFO?.surveys?.wells && Object.keys(currentRTFO?.surveys?.wells);
  
      if (!wells?.length) return coords;
  
      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = currentRTFO?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellCoordinates = surveyToCoordinates(lat, lon, surveyData);
  
        if (!coords[wells[i]]) coords[wells[i]] = [];
  
        coords[wells[i]] = wellCoordinates;
      };
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };
  }, [currentRTFO?.surveys?.wells]);

  const bufferZoneCoordinates = useMemo(() => {
    try {
      if (!wellCoordinates || Object.keys(wellCoordinates)?.length === 0) return []
  
      const bufferZoneCoord = []
      const buffers = []
  
      for (const wellName of Object.keys(wellCoordinates)) {
        for (const point of wellCoordinates[wellName]) {
          const center = [point?.lon, point?.lat];
          const radius = 5;
          const options = {steps: 64, units: 'kilometers'};
          const circle = turf.circle(center, radius, options);
          
          buffers[wellName] = circle
      
  
          buffers.push(circle);
        }
  
      }
  
      // Combine all circle geometries
      const combinedCircles = turf.combine(turf.featureCollection(buffers));

      // Create a convex hull from the combined circles
      const convexHull = turf.convex(combinedCircles);

      for (const point of convexHull?.geometry?.coordinates?.[0]) {
        bufferZoneCoord.push([point[1], point[0]])
      }
  
      return bufferZoneCoord;

    } catch (error) {
      console.log(error)
    }

  }, [wellCoordinates]);

  const fpzCoordinates = useMemo(() => {
    try {
      if (!wellCoordinates || Object.keys(wellCoordinates)?.length === 0) return []
  
      const fpzCoord = []
      const buffers = []
  
      for (const wellName of Object.keys(wellCoordinates)) {
        for (const point of wellCoordinates[wellName]) {
          const center = [point?.lon, point?.lat];
          const radius = 1;
          const options = {steps: 64, units: 'kilometers'};
          const circle = turf.circle(center, radius, options);
          buffers.push(circle);
        }
      }
  
      // Combine all circle geometries
      const combinedCircles = turf.combine(turf.featureCollection(buffers));

      // Create a convex hull from the combined circles
      const convexHull = turf.convex(combinedCircles);

      for (const point of convexHull?.geometry?.coordinates?.[0]) {
        fpzCoord.push([point[1], point[0]])
      }
  
      return fpzCoord;

    } catch (error) {
      console.log(error)
    }

  }, [wellCoordinates]);

	const globalCenter = useMemo(() => {
    try {
      const center = turf.center(turf.points(bufferZoneCoordinates))
      return {lat: center?.geometry?.coordinates?.[0], lon: center?.geometry?.coordinates?.[1]};
    } catch (error) {
      console.log(error);
      return {lat: 0, lon: 0};
    };
  }, [bufferZoneCoordinates]);

	return (
		<div style={{height: 1250, width: 1250, zIndex: 999, position: 'absolute', top: '25%', left: '25%'}} id="map" >
			<MapContainer center={[globalCenter?.lat, globalCenter?.lon]} zoom={13} maxZoom={17} style={{height: 1250, width: 1250}} ref={mapRef} attributionControl={false} zoomControl={false}>
        
				{/* <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false" pane={"tilePane"} /> */}
				<TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`} removeAttribution attributionControl="false" pane={"tilePane"} />

        {bufferZoneCoordinates && <Polygon positions={bufferZoneCoordinates} pathOptions={{fillOpacity: 0.75, color: 'red', fillColor: 'transparent'}} />}
        {fpzCoordinates && <Polygon positions={fpzCoordinates} pathOptions={{fillOpacity: 0.25, color: 'transparent', fillColor: 'grey'}} />}

				{Object.keys(wellCoordinates)?.map((well, index) => {
					return (
						<div key={`${well}-${index}`}>
							<Polyline pathOptions={{ color: 'black', weight: 6 }} positions={wellCoordinates[well]?.map(w => [w.lat, w.lon])} bounds={L.latLngBounds(wellCoordinates[well]?.map(w => [w.lat, w.lon]))} />
							<CircleMarker center={[currentRTFO?.surveys?.wells?.[well]?.surfaceLat, currentRTFO?.surveys?.wells?.[well]?.surfaceLon]} pathOptions={{ color: 'black' }} radius={10}>
							</CircleMarker>
              <WellLabel center={[wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lon, wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]?.lat]} wellName={well}/>
						</div>
					)
				})}

        <RTFOMapLegend darkMode={false} minColorValue={0} maxColorValue={5} metric={''} showMetric={false} showSeismic={true}/>

        <MapScreenshoter seismicReportData={seismicReportData} setSeismicReportMapVisible={setSeismicReportMapVisible} setSeismicReportMap={setSeismicReportMap} />

			</MapContainer>
		</div>
	)
})

const MapScreenshoter = ({ seismicReportData, setSeismicReportMapVisible, setSeismicReportMap }) => {
  const map = useMap();
  
  const [currentEventIndex, setCurrentEventIndex] = useState(0)

  const snapshotOptions = {
    hideElementsWithSelectors: [],
    position: "topright",
    hidden: true,
  };
  const simpleMapScreenshoter = new SimpleMapScreenshoter(snapshotOptions).addTo(map);

  const eventsMaps = useRef({});

  useEffect(() => {

    if (seismicReportData?.length > 0 && currentEventIndex < seismicReportData.length) {
      const event = seismicReportData[currentEventIndex];

      if (eventsMaps.current[event.id]) {
        setCurrentEventIndex(currentEventIndex + 1);
        return;
      };

      const circle = L.circle([event?.latitude, event?.longitude], {
        color: 'black',
        weight: 2,
        fillColor: getSeismicEventColor(event?.localMagnitude),
        fillOpacity: 0.75,
        radius: 200
      }).addTo(map);

      captureScreenshot().then(imgData => {
        eventsMaps.current[event.id] = imgData

        setTimeout(() => {
          circle.remove()
          setCurrentEventIndex(currentEventIndex + 1);

          if (currentEventIndex === seismicReportData?.length - 1) {
            console.log('All events processed!');
            setSeismicReportMap(eventsMaps.current)
            setSeismicReportMapVisible(false)
          }
        }, 500)
      })

    };
    

    // return () => simpleMapScreenshoter.remove();
    // eslint-disable-next-line
  }, [currentEventIndex, seismicReportData]);
  
  const captureScreenshot = () => {
    return new Promise(resolve => {
      setTimeout(() => {
        simpleMapScreenshoter.takeScreen('image')
        .then(imageURL => {
          resolve(imageURL)
        })
      }, 1000);
    })
  }

};

function WellLabel({ center, wellName }) {
  try {
    const radius = 1;
    const options = {steps: 16, units: 'kilometers'};
    const circle = turf.circle(center, radius, options);
    const coords = circle?.geometry?.['coordinates']?.[0]?.map(c => [c?.[1], c?.[0]]);

    return (
      <SVGOverlay
        bounds={L.latLngBounds(coords)}
        attributes={{}}
      > 
        <text x={'50%'} y={'50%'} fontSize="16px" textAnchor="start" dominantBaseline="middle" strokeWidth="2px" paintOrder="stroke" stroke="white" fill="red" >
          {wellName}
        </text>
      </SVGOverlay>
    );

  } catch (error) {
    return <></>
  };
  
};

export default RTFOSeismicReportMap;
